import React, { Fragment } from "react";
import { Button, Grid, Icon, Label, Popup } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import location from "../../../icons/location.svg";
import person from "../../../icons/person.svg";
import { Application } from "../types";
import GenderImage from "../../../components/GenderImage";
import { ApplicationStatus } from "../../../app/enums/applicationStatus";
import { HasRole } from "../../../app/helpers/userHelpers";
import { UserRoles } from "../../../app/auth/roles";
import agent from "../../../app/api/agent";
import { formatGradeWithSuffix } from "../../../app/helpers/gradeHelpers";
import ButtonLink from "../../../components/ButtonLink";
import Multiline from "../../../components/Multiline";
import { downloadFile } from "../../../app/helpers/fileHelper";

interface IProps {
  application: Application;
  handleConfirmation: (applicationId: number, approved: boolean) => void;
  handleCancellation: (applicationId: number) => void;
}

const StudentApplicationItem: React.FC<IProps> = ({ application, handleConfirmation, handleCancellation }) => {
  const { t } = useTranslation();

  const student = application.student;
  const isSchool = HasRole(UserRoles.school);
  const isCompany = HasRole(UserRoles.company);
  const alreadyApproved = application.status === ApplicationStatus.COMPANY_APPROVED;
  const cancelled = [ApplicationStatus.STUDENT_CANCELLED, ApplicationStatus.COMPANY_CANCELLED].includes(
    application.status
  );

  const downloadCv = async () => {
    const response = await agent.Files.getCv(application.cvAsset);
    downloadFile(response);
  };

  return (
    <Grid.Row>
      <Grid.Column width={2}>
        <GenderImage gender={student.gender} />
      </Grid.Column>
      <Grid.Column width={14}>
        <div className="name">
          {student.contactInfo.fullName}, {formatGradeWithSuffix(student.grade, student.isStudent)}
          {cancelled && (
            <Label color="red" size="small" style={{ marginLeft: "0.5em", verticalAlign: "middle" }}>
              {t(`applications.status.${application.status}`)}
            </Label>
          )}
        </div>
        <div className="details">
          <span>
            <img src={location} alt="location" />
            {student.school}
          </span>
          {!cancelled && (
            <>
              <span>
                <img src={person} alt="person" />
                {student.contactInfo.email}
              </span>
              {student.contactInfo.phoneNumber && (
                <span>
                  <Icon name="mobile alternate" />
                  {student.contactInfo.phoneNumber}
                </span>
              )}
              {application.allowPhotos !== null && (
                <Popup
                  content={
                    application.allowPhotos ? "Sutinka būti fotografuojamas(-a)" : "Nesutinka būti fotografuojamas(-a)"
                  }
                  size="small"
                  trigger={
                    <span className={application.allowPhotos ? undefined : "photo-consent--denied"}>
                      <Icon name="camera" />
                    </span>
                  }
                />
              )}
            </>
          )}
        </div>
        {!cancelled && (
          <>
            <div className="motivation-letter">
              <Multiline value={application.motivationLetter} />
            </div>
            <div className="student-application-actions">
              {application.linkedinUrl && (
                <a href={application.linkedinUrl} target="blank">
                  LinkedIn
                </a>
              )}
              {application.cvAsset && (
                <ButtonLink onClick={downloadCv}>{t("applications.company.actions.download_cv")}</ButtonLink>
              )}
              {isSchool && alreadyApproved && (
                <ButtonLink onClick={() => handleCancellation(application.id)}>
                  {t("applications.company.actions.cancel_visit")}
                </ButtonLink>
              )}
            </div>
          </>
        )}
        {!cancelled && !alreadyApproved && (
          <Fragment>
            <Button primary onClick={() => handleConfirmation(application.id, true)}>
              {t("applications.company.actions.accept")}
            </Button>
            <Button negative onClick={() => handleConfirmation(application.id, false)}>
              {t("applications.company.actions.reject")}
            </Button>
          </Fragment>
        )}
      </Grid.Column>
    </Grid.Row>
  );
};

export default StudentApplicationItem;
